import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import AccountHistoryController from "../../../controller/accounts/accountHistoryController";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import AppConstants from "../../../utils/appConstants";
import { parseLocalDateFromYmd } from "../../../utils/dateRangeApiFormat";
import { getPermissionControllers, isAccountPermissionDisabled, isAccountApprovalLocked } from "../../../utils/permissionHelper";
import "../../css/List.css";
import "../../css/TransactionsHistory.css";
import "../../css/AccountHistory.css";

const formatVolume = (value) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatPrice = (value, digits = 5) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "-";
  const precision = Number.isFinite(Number(digits)) && Number(digits) > 0 ? Number(digits) : 5;
  return num.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

const formatProfit = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const abs = Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${num >= 0 ? "+" : "-"}${abs} $`;
};

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const abs = Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${abs} $`;
};

const formatCloseTime = (timestamp, fallback) => {
  const numeric = Number(timestamp);
  let date = null;

  if (Number.isFinite(numeric) && numeric > 0) {
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    date = new Date(ms);
  } else if (fallback) {
    const raw = String(fallback).trim();
    const withT = raw.includes("T") ? raw : raw.replace(" ", "T");
    date = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(withT) ? withT : `${withT}Z`);
  }

  if (!date || Number.isNaN(date.getTime())) return "-";

  // MT5 deal Time is server/UTC wall clock. Do not shift it again into the
  // browser timezone (that added ~3 hours for UTC+3).
  return date.toLocaleString(undefined, {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const formatExecutionTime = formatCloseTime;

const HISTORY_COLUMN_CLASSES = [
  "history-col-symbol",
  "history-col-type",
  "history-col-volume",
  "history-col-open",
  "history-col-close",
  "history-col-tp",
  "history-col-sl",
  "history-col-charges",
  "history-col-close-time",
  "history-col-profit",
];

const toDateInputValue = (v) => {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AccountHistory = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const navigate = useNavigate();
  const { tradingId: tradingIdParam } = useParams();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [userReady, setUserReady] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ dateFrom: null, dateTo: null, presetId: null });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    dateFrom: null,
    dateTo: null,
  });
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10, total: 0, lastPage: 1 });
  const perPage = 10;
  const loadRequestIdRef = useRef(0);
  const [dateFieldErrors, setDateFieldErrors] = useState({ dateFrom: false, dateTo: false });

  const tradingId =
    location.state?.tradingId || decodeURIComponent(tradingIdParam || "");

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData") || localStorage.getItem("userData");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (_) {
      setUser(null);
    } finally {
      setUserReady(true);
    }
  }, []);

  const canViewAccountHistory = useMemo(() => {
    if (!user) return false;
    const { customerPerm } = getPermissionControllers(user);
    return (
      !isAccountApprovalLocked(user) &&
      !isAccountPermissionDisabled(
        customerPerm,
        PermissionCustomerModelController.PER__CAN_VIEW_ACCOUNT_HISTORY
      )
    );
  }, [user]);

  useEffect(() => {
    if (!userReady) return;

    if (!canViewAccountHistory) {
      setRows([]);
      setIsLoading(false);
      setError(t("dont_have_permissions", { ns: "mobile" }));
      return;
    }

    if (!tradingId || tradingId === "-") {
      setRows([]);
      setError(null);
      return;
    }

    const requestId = ++loadRequestIdRef.current;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await AccountHistoryController.getHistory({
          tradingId,
          page: currentPage,
          perPage,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        if (requestId !== loadRequestIdRef.current) return;
        setRows(result.items || []);
        setPagination(result.pagination || { page: currentPage, perPage, total: 0, lastPage: 1 });
      } catch (e) {
        if (requestId !== loadRequestIdRef.current) return;
        setRows([]);
        const isNeedPermission = e?.code === AppConstants.responseNeedPermission;
        setError(
          isNeedPermission
            ? t("dont_have_permissions", { ns: "mobile" })
            : e?.message ||
              t("account_history_load_error", {
                ns: "mobile",
                defaultValue: "Could not load account history.",
              })
        );
      } finally {
        if (requestId === loadRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    load();
  }, [userReady, canViewAccountHistory, tradingId, currentPage, perPage, appliedFilters, t]);

  const applyFilters = () => {
    if (!requireDateRange()) return;
    setCurrentPage(1);
    setAppliedFilters({
      search: searchTerm.trim(),
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    });
  };

  const resetFilters = () => {
    setSearchTerm("");
    setDateRange({ dateFrom: null, dateTo: null, presetId: null });
    setDateFieldErrors({ dateFrom: false, dateTo: false });
    setCurrentPage(1);
    setAppliedFilters({ search: "", dateFrom: null, dateTo: null });
  };

  const requireDateRange = () => {
    const missingFrom = !dateRange.dateFrom;
    const missingTo = !dateRange.dateTo;
    setDateFieldErrors({ dateFrom: missingFrom, dateTo: missingTo });
    if (missingFrom || missingTo) {
      return false;
    }
    return true;
  };

  const totalPages = Math.max(1, Number(pagination.lastPage) || 1);
  const totalRecords = Number(pagination.total) || 0;

  return (
    <div className="accounts-list-page accounts-list-standard-page account-history-page transactions-centered-page transactions-history-page">
      <Sidebar user={user} />
      <div className="accounts-list-content">
        <div className="transactions-history-filters">
          <div className="transactions-filter-bar w-100" aria-label="Filter account history">
            <div className="transactions-filter-row">
              <div className="transactions-filter-field transactions-filter-field--search">
                <label>{t("search", { ns: "mobile" })}</label>
                <div className="transactions-filter-input-wrap">
                  <i className="fa-solid fa-magnifying-glass" aria-hidden />
                  <input
                    type="text"
                    className="transactions-filter-input"
                    placeholder={t("search", { ns: "mobile" })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyFilters();
                    }}
                  />
                </div>
              </div>
              <div className="transactions-filter-field">
                <label>From Date</label>
                <input
                  type="date"
                  className={`transactions-filter-input${
                    dateFieldErrors.dateFrom ? " transactions-filter-input--error" : ""
                  }`}
                  aria-invalid={dateFieldErrors.dateFrom}
                  value={toDateInputValue(dateRange.dateFrom)}
                  onChange={(e) => {
                    const dateFrom = parseLocalDateFromYmd(e.target.value);
                    setDateRange((p) => ({
                      ...p,
                      dateFrom,
                    }));
                    if (dateFrom) {
                      setDateFieldErrors((p) => ({ ...p, dateFrom: false }));
                    }
                  }}
                />
              </div>
              <div className="transactions-filter-field">
                <label>To Date</label>
                <input
                  type="date"
                  className={`transactions-filter-input${
                    dateFieldErrors.dateTo ? " transactions-filter-input--error" : ""
                  }`}
                  aria-invalid={dateFieldErrors.dateTo}
                  value={toDateInputValue(dateRange.dateTo)}
                  onChange={(e) => {
                    const dateTo = parseLocalDateFromYmd(e.target.value);
                    setDateRange((p) => ({
                      ...p,
                      dateTo,
                    }));
                    if (dateTo) {
                      setDateFieldErrors((p) => ({ ...p, dateTo: false }));
                    }
                  }}
                />
              </div>
              <div className="transactions-filter-actions">
                <button
                  type="button"
                  className={`transactions-filter-apply${
                    dateFieldErrors.dateFrom || dateFieldErrors.dateTo
                      ? " transactions-filter-apply--error"
                      : ""
                  }`}
                  aria-invalid={dateFieldErrors.dateFrom || dateFieldErrors.dateTo}
                  onClick={applyFilters}
                >
                  Filter
                </button>
                <button type="button" className="transactions-filter-reset" onClick={resetFilters}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="accounts-table-container container-fluid px-0 d-flex flex-column">
          <div className="table-top-header d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div className="accounts-list-title-wrapper">
              <h1 className="accounts-list-title">
                {t("history_of_account", { ns: "mobile", defaultValue: "History of Account" })}
              </h1>
              {tradingId && tradingId !== "-" ? (
                <span className="account-history-trading-id">#{tradingId}</span>
              ) : null}
            </div>
            <div className="accounts-list-actions d-flex flex-wrap align-items-center">
              <button className="new-account-btn" type="button">
                <i className="fa-solid fa-plus"></i>{" "}
                {t("export_history", { ns: "mobile", defaultValue: "Export History" })}
              </button>
            </div>
          </div>

          <div className="table-responsive accounts-table-scroll">
            <div className="accounts-table account-history-table">
              <div className="table-header-row">
                <div className="table-header-cell history-col-symbol">
                  {t("symbol", { ns: "mobile", defaultValue: "Symbol" })}
                </div>
                <div className="table-header-cell history-col-type">
                  {t("type", { ns: "mobile" })}
                </div>
                <div className="table-header-cell history-col-volume">
                  {t("volume", { ns: "mobile", defaultValue: "Volume" })}
                </div>
                <div className="table-header-cell history-col-open">
                  {t("open_price", { ns: "mobile", defaultValue: "Open Price" })}
                </div>
                <div className="table-header-cell history-col-close">
                  {t("close_price", { ns: "mobile", defaultValue: "Close Price" })}
                </div>
                <div className="table-header-cell history-col-tp">
                  {t("take_profit", { ns: "mobile", defaultValue: "Take Profit" })}
                </div>
                <div className="table-header-cell history-col-sl">
                  {t("stop_loss", { ns: "mobile", defaultValue: "Stop Loss" })}
                </div>
                <div className="table-header-cell history-col-charges">
                  {t("commission", { ns: "mobile", defaultValue: "Commission" })}
                </div>
                <div className="table-header-cell history-col-close-time">
                  {t("close_time", { ns: "mobile", defaultValue: "Close Time" })}
                </div>
                <div className="table-header-cell history-col-profit">
                  {t("profit", { ns: "mobile", defaultValue: "Profit" })}
                </div>
              </div>

              {isLoading &&
                Array.from({ length: 6 }).map((_, rowIndex) => (
                  <div className="table-row" key={`sk-row-${rowIndex}`}>
                    {HISTORY_COLUMN_CLASSES.map((colClass) => (
                      <div className={`table-cell ${colClass}`} key={`sk-cell-${rowIndex}-${colClass}`}>
                        <span className="accounts-skeleton-bar accounts-skeleton-bar--cell" />
                      </div>
                    ))}
                  </div>
                ))}

              {!isLoading && error && (
                <div className="table-row no-data-row">
                  <div className="table-cell no-data" style={{ width: "100%" }}>
                    {error}
                  </div>
                </div>
              )}

              {!isLoading && !error && rows.length === 0 && (
                <div className="table-row no-data-row">
                  <div className="table-cell no-data" style={{ width: "100%" }}>
                    {t("no_history_found", { ns: "mobile", defaultValue: "No history records found" })}
                  </div>
                </div>
              )}

              {!isLoading &&
                !error &&
                rows.map((row) => (
                  <div className="table-row" key={row.id || `${row.symbol}-${row.closeTime}`}>
                    <div className="table-cell history-col-symbol">
                      <span className="account-history-symbol">{row.symbol || "-"}</span>
                    </div>
                    <div className="table-cell history-col-type">
                      <span
                        className={`type-badge ${
                          row.type === "SELL" ? "type-history-sell" : "type-history-buy"
                        }`}
                      >
                        {row.type === "SELL"
                          ? t("sell", { ns: "mobile", defaultValue: "Sell" })
                          : t("buy", { ns: "mobile", defaultValue: "Buy" })}
                      </span>
                    </div>
                    <div className="table-cell history-col-volume">{formatVolume(row.volume)}</div>
                    <div className="table-cell history-col-open">
                      {formatPrice(row.openPrice, row.digits)}
                    </div>
                    <div className="table-cell history-col-close">
                      {formatPrice(row.closePrice, row.digits)}
                    </div>
                    <div className="table-cell history-col-tp history-muted">
                      {formatPrice(row.takeProfit, row.digits)}
                    </div>
                    <div className="table-cell history-col-sl history-muted">
                      {formatPrice(row.stopLoss, row.digits)}
                    </div>
                    <div className="table-cell history-col-charges history-muted">
                      {formatMoney(row.charges)}
                    </div>
                    <div className="table-cell history-col-close-time history-muted">
                      {formatCloseTime(row.closeTimestamp, row.closeTime)}
                    </div>
                    <div className="table-cell history-col-profit">
                      <span
                        className={
                          row.profit >= 0
                            ? "account-history-profit--positive"
                            : "account-history-profit--negative"
                        }
                      >
                        {formatProfit(row.profit)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="accounts-pagination accounts-pagination--with-summary accounts-pagination--opposite-dark">
            <p className="ib-clients-pagination-summary">
              {t("showing_history_records", {
                ns: "mobile",
                count: totalRecords,
                defaultValue: `Showing ${totalRecords} history records`,
              })}
            </p>
            <div className="pagination-controls accounts-pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
                type="button"
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-right" : "fa-chevron-left"}`}></i>
              </button>
              <button className="pagination-btn active" type="button">
                <span>{currentPage}</span>
              </button>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages || isLoading}
                type="button"
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-left" : "fa-chevron-right"}`}></i>
              </button>
            </div>
          </div>
        </div>
        <div className="account-history-back-row">
          <button
            type="button"
            className="account-history-back-btn"
            onClick={() => navigate("/pages/accounts/list")}
          >
            <i className={`fa-solid ${isRtl ? "fa-arrow-right" : "fa-arrow-left"}`} aria-hidden />
            {t("back_to_accounts", { ns: "mobile", defaultValue: "Back to accounts" })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountHistory;
